import type { ConceptMapData, BloomLevel } from '../types'

// Map 6-level Bloom to 3 user-visible levels
const BLOOM_DISPLAY_ORDER = ['application', 'understanding', 'knowledge', 'not_started'] as const
type DisplayBloom = typeof BLOOM_DISPLAY_ORDER[number]

const BLOOM_LABELS: Record<DisplayBloom, { da: string; en: string }> = {
  application: { da: 'Anvendelse', en: 'Application' },
  understanding: { da: 'Forståelse', en: 'Understanding' },
  knowledge: { da: 'Viden', en: 'Knowledge' },
  not_started: { da: 'Ikke startet', en: 'Not started' },
}

const BLOOM_TO_DISPLAY: Record<BloomLevel, DisplayBloom> = {
  create: 'application',
  evaluate: 'application',
  analyze: 'application',
  apply: 'application',
  understand: 'understanding',
  recognize: 'knowledge',
}

const RELATION_SYMBOLS: Record<string, string> = {
  causal: '→',
  interdependent: '↔',
  related: '—',
}

/**
 * Generates a markdown skeleton from a concept map for preparation notes.
 *
 * Concepts are grouped by Bloom level (Application > Understanding > Knowledge > Not started).
 * Relations are shown as blockquote context lines under each concept.
 *
 * @param conceptMap The concept map containing nodes and edges
 * @param language 'da' for Danish, 'en' for English (default: 'da')
 * @returns A markdown string skeleton ready for note-taking
 */
export function generateNoteSkeleton(
  conceptMap: ConceptMapData,
  language: 'da' | 'en' = 'da'
): string {
  // Group nodes by display Bloom level
  const grouped = new Map<DisplayBloom, typeof conceptMap.nodes>()

  for (const level of BLOOM_DISPLAY_ORDER) {
    grouped.set(level, [])
  }

  for (const node of conceptMap.nodes) {
    const display = BLOOM_TO_DISPLAY[node.bloomLevel] ?? 'not_started'
    grouped.get(display)!.push(node)
  }

  // Build relations lookup: nodeId → descriptions
  const relations = new Map<string, string[]>()
  for (const edge of conceptMap.edges) {
    const relationType: string = edge.relationType ?? 'related'
    const symbol: string = RELATION_SYMBOLS[relationType] ?? '—'
    const sourceNode = conceptMap.nodes.find(n => n.id === edge.source)
    const targetNode = conceptMap.nodes.find(n => n.id === edge.target)
    if (sourceNode && targetNode) {
      const sourceRels = relations.get(edge.source) ?? []
      sourceRels.push(`${symbol} ${relationType} ${language === 'da' ? 'til' : 'to'} ${targetNode.label}`)
      relations.set(edge.source, sourceRels)

      const targetRels = relations.get(edge.target) ?? []
      const reverseSymbol = relationType === 'causal' ? '←' : symbol
      targetRels.push(`${reverseSymbol} ${relationType} ${language === 'da' ? 'fra' : 'from'} ${sourceNode.label}`)
      relations.set(edge.target, targetRels)
    }
  }

  const placeholder = language === 'da' ? 'Skriv dine noter her...' : 'Write your notes here...'
  const lines: string[] = []

  for (const level of BLOOM_DISPLAY_ORDER) {
    const nodes = grouped.get(level)!
    if (nodes.length === 0 && level !== 'not_started') continue

    lines.push(`## ${BLOOM_LABELS[level][language]}`)
    lines.push('')

    if (nodes.length === 0) {
      lines.push('')
      continue
    }

    for (const node of nodes) {
      lines.push(`### ${node.label}`)
      const nodeRels = relations.get(node.id)
      if (nodeRels && nodeRels.length > 0) {
        for (const rel of nodeRels) {
          lines.push(`> ${rel}`)
        }
      }
      lines.push('')
      lines.push(placeholder)
      lines.push('')
    }
  }

  return lines.join('\n').trim() + '\n'
}
